import api from "../api";
import { logError } from "./logError";

const defaultPageSize = 1000;

// Backend's fastifyPaging middleware hard-clamps every request to 1000 rows regardless of
// the requested pageSize. Honor that cap per request and paginate client-side to reach the
// caller's requested total.
const BACKEND_PAGE_CAP = 1000;

class Dialogs {
	/**
	 * `pageSize` is the caller's overall ceiling. Stays at 1000 by default so dropdowns that
	 * search the backend on every keystroke (FABCustomerAutoComplete, ActionSchedule,
	 * ActionVideo) keep a single-request profile. Callers that need the full org roster pass
	 * a larger value (ShareSheetDialog, ShareActionDialog) — we then fetch in 1000-row
	 * chunks and stop when we've reached pageSize, the server's reported count, or a short
	 * page. On mid-loop failure we return what we collected so a transient blip on page N
	 * doesn't throw away pages 1..N-1, and surface the error via logError for Datadog.
	 */
	static async getCustomers(searchTerm = "", pageSize = defaultPageSize, { throwOnError = false } = {}) {
		searchTerm = searchTerm || "";
		let order = [["lastname", "ASC NULLS LAST", ["empties.last"]]];
		let terms = searchTerm !== "" ? searchTerm.split(" ") : [];
		let q = [];
		for (let i = 0; i < terms.length; i++) {
			let term = terms[i];
			q.push({
				$or: [
					{
						firstname: {
							$ilike: "%" + term + "%"
						}
					},
					{
						lastname: {
							$ilike: "%" + term + "%"
						}
					},
					{
						"$EmailAddresses.emailAddress$": {
							$ilike: "%" + term + "%"
						}
					},
					{
						"$PhoneNumbers.phonenumberraw$": {
							$ilike: "%" + term + "%"
						}
					}
				]
			});
		}

		const whereCondition = terms.length > 0 ? { $and: q } : {};
		const perPage = Math.min(pageSize, BACKEND_PAGE_CAP);
		const rows = [];
		let page = 1;

		try {
			while (rows.length < pageSize) {
				const result = await api.getCustomers({
					page,
					pageSize: perPage,
					where: whereCondition,
					order
				});
				const pageRows = result?.rows ?? [];
				rows.push(...pageRows);

				if (pageRows.length < perPage) break;
				if (typeof result?.count === "number" && rows.length >= result.count) break;
				page += 1;
			}
		} catch (error) {
			logError("Dialogs.getCustomers: pagination failed mid-loop, returning partial results", error, {
				page,
				collected: rows.length,
				ceiling: pageSize
			});
			if (throwOnError) throw error;
		}

		return rows.slice(0, pageSize);
	}
}

export default Dialogs;
